import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import {BoxArrowRight, List, PencilSquare, PersonFill, XLg} from "react-bootstrap-icons";
import {useLocation, useNavigate} from "react-router-dom";

import useScreenWidth from "../../hooks/useScreenWidth";
import useUser from "../../hooks/useUser";
import { logout } from "../../http/userAPI";
import LogoutAlert from "../UI/MyAlert/LogoutAlert";
import MyModal from "../UI/MyModal/MyModal";
import Login from "../Forms/Login";
import ResetPasswordForm from "../Forms/ResetPasswordForm";

import './Navbar.css';

const Navbar: React.FC = observer(() => {
    const [мodalVisible, setModalVisible] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<'login' | 'reset'>();
    const [isNavbarOpen, setNavbarOpen] = useState<boolean>(false);
    const [isLogoutAlertVisible, setLogoutAlertVisible] = useState<boolean>(false);
    const user = useUser();
    const screenWidth = useScreenWidth();
    const navigate = useNavigate();
    const mainContainerRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const navbarMainContentRef = useRef<HTMLDivElement>(null);
    const navbarMainButtonsRef = useRef<HTMLDivElement>(null);
    const [mainContentHeight, setMainContentHeight] = useState<number | null>(null);

    useEffect(() => {
        if (navbarMainButtonsRef.current) {
            const height = navbarMainButtonsRef.current.offsetHeight;
            setMainContentHeight(height);
        }
    }, [isNavbarOpen, user.isAuth, screenWidth]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                mainContainerRef.current &&
                !mainContainerRef.current.contains(event.target as Node)
            ) {
                setNavbarOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    
    const handleModalVisible = () => {
        setModalVisible(!мodalVisible);
    }
    
    const handleChangeModalContent = (modalType: 'login' | 'reset') => {
        setModalContent(modalType);
    }
    
    const toggleNavbar = () => {
        setNavbarOpen(!isNavbarOpen);
    }
    
    const handleLogout = () => {
        try {
            logout();
            user?.logout();
        } catch {
            console.log('Произошла ошибка при выходе из аккаунта.');
        } finally {
            navigate('/');
        }
    };
    
    return (
        <>
            <div className="navbar" ref={mainContainerRef}>
                <h3 className='navbar_title' onClick={() => navigate('/')}>
                    miartcom
                </h3>

                <div className='navbar_buttons'>
                    {screenWidth > 1200 && user.isAuth && user.user?.firstname && (
                        <div className='navbar_buttons_userInfoContainer'>
                            <span className='navbar_username'>{user.user?.firstname}</span>
                        </div>
                    )}

                    {screenWidth > 1200 && location.pathname === '/profile' && (
                        <PencilSquare className='navbar_button edit' onClick={() => navigate('/updateProfile')} title='Редактировать профиль'/>
                    )}

                    {(user.isAuth && screenWidth > 1200) && (
                        <PersonFill className='navbar_button' onClick={() => navigate('profile')}/>
                    )}
                    <List className='navbar_button' onClick={toggleNavbar}/>
                </div>

                <div className='navbar_buttons_admin'>
                    {(screenWidth > 1200 && user.isAuth && user.isAdmin) && (
                        <span className='navbar_admin' onClick={() => navigate('/admin-panel')}>Администратор</span>
                    )}
                </div>

                <div className={`navbar_mainContent ${isNavbarOpen ? 'open' : ''}`}>
                    {screenWidth > 1200 && (
                        <h3 className='navbar_title' onClick={() => navigate('/')}>
                            miartcom
                        </h3>
                    )}

                    {screenWidth <= 1200 && isNavbarOpen ? (
                        <div className='navbar_mainContent_content'>
                            <div className="navbar_mainContent_links">
                                {(user.isAuth && user.isAdmin) && (
                                    <div className={`navbar_mainContent_link ${location.pathname === '/admin-panel' ? 'active' : ''}`}>
                                        <span className='navbar_mainContent_link'
                                            onClick={() => navigate('/admin-panel')}>Администратор</span>
                                    </div>
                                )}
                                <div className={`navbar_mainContent_link ${location.pathname === '/' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/')}>Главная</span>
                                </div>
                                <div
                                    className={`navbar_mainContent_link ${location.pathname === '/events' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/events')}>Афиша</span>
                                </div>
                                <div
                                    className={`navbar_mainContent_link ${location.pathname === '/biography' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/biography')}>Биография</span>
                                </div>
                                <div
                                    className={`navbar_mainContent_link ${location.pathname === '/gallery' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/gallery')}>Галерея</span>
                                </div>
                                <div
                                    className={`navbar_mainContent_link ${location.pathname === '/contacts' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/contacts')}>Контакты</span>
                                </div>
                            </div>

                            {!user.isAuth && (
                                <div className="navbar_MainButtons">
                                    <button
                                        onClick={() => navigate('/reg')}
                                        className='navbar_MainButton'
                                    >Регистрация
                                    </button>

                                    <button
                                        onClick={() => {
                                            handleModalVisible();
                                            setModalContent('login');
                                            setNavbarOpen(false)
                                        }}
                                        className='navbar_MainButton'
                                    >Войти
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : screenWidth > 1200 ? (
                        <div
                            className='navbar_mainContent_content'
                            ref={navbarMainContentRef}
                            style={{height: mainContentHeight ? `${mainContentHeight}px` : 'auto'}}
                        >
                            <div className="navbar_mainContent_links">
                                <div className={`navbar_mainContent_link ${location.pathname === '/' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/')}>Главная</span>
                                </div>
                                <div
                                    className={`navbar_mainContent_link ${location.pathname === '/events' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/events')}>Афиша</span>
                                </div>
                                <div
                                    className={`navbar_mainContent_link ${location.pathname === '/biography' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/biography')}>Биография</span>
                                </div>
                                <div
                                    className={`navbar_mainContent_link ${location.pathname === '/gallery' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/gallery')}>Галерея</span>
                                </div>
                                <div
                                    className={`navbar_mainContent_link ${location.pathname === '/contacts' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/contacts')}>Контакты</span>
                                </div>
                                <div
                                    className={`navbar_mainContent_link ${location.pathname === '/tour' ? 'active' : ''}`}>
                                    <span onClick={() => navigate('/tour')}>3D Тур</span>
                                </div>
                            </div>

                            {!user.isAuth && (
                                <div className="navbar_MainButtons" ref={navbarMainButtonsRef}>
                                    <button
                                        onClick={() => navigate('/reg')}
                                        className='navbar_MainButton'
                                    >Регистрация
                                    </button>

                                    <button
                                        onClick={() => {
                                            handleModalVisible();
                                            setModalContent('login');
                                        }}
                                        className='navbar_MainButton'
                                    >Войти
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <></>
                    )}

                    <div className='navbar_buttons'>
                        {user.isAuth && (
                            <>
                                {screenWidth <= 1200 &&
                                    <PencilSquare className='navbar_button' onClick={() => navigate('/updateProfile')} title='Редактировать профиль'/>}
                                <PersonFill className='navbar_button' onClick={() => navigate('profile')}/>
                                <BoxArrowRight className='navbar_button' onClick={() => {
                                    setLogoutAlertVisible(true);
                                    setNavbarOpen(false);
                                }}/>
                            </>
                        )}
                        <XLg className='navbar_button' onClick={toggleNavbar}/>
                    </div>

                </div>
            </div>

            <MyModal visible={мodalVisible} setVisible={setModalVisible}>
                <div className="regOrLoginModal">
                    {modalContent === 'login' && (
                        <>
                            <Login closeModal={() => setModalVisible(false)}/>
                            <p onClick={() => {
                                navigate('/reg');
                                handleModalVisible();
                            }}>Регистрация</p>
                            <p className="login" onClick={() => handleChangeModalContent('reset')}>Забыли пароль?</p>
                        </>
                    )}
                    {modalContent === 'reset' && (
                        <>
                            <ResetPasswordForm closeModal={() => setModalVisible(false)}/>
                            <p onClick={() => handleChangeModalContent('login')}>Войти</p>
                        </>
                    )}
                </div>
            </MyModal>

            <LogoutAlert visible={isLogoutAlertVisible} setVisible={setLogoutAlertVisible} />
        </>
    )
})

export default Navbar;